/**
 * chatStreamMachine — per-chat streaming lifecycle (state topology only).
 *
 * One actor per chat, owned by [MachineRegistry](./machineRegistry.ts) and NOT by
 * any component — which is what lets chat A keep progressing while chat B is
 * mounted.
 */

import { assign, setup } from 'xstate';

import type { CHAT_STREAM_STATE } from './actions';
import {
  CHAT_STREAM_EVENT,
  chatStreamAssigns,
  type ChatStreamContext,
  chatStreamEffects,
  type ChatStreamEvent,
  type ChatStreamInput
} from './actions';

export type { ChatStreamContext, ChatStreamEvent, ChatStreamInput };

const _STATE_NAMES: Record<CHAT_STREAM_STATE, true> = {
  idle: true,
  connecting: true,
  streaming: true,
  done: true,
  error: true,
  cancelling: true,
  aborted: true
};
void _STATE_NAMES;

export const chatStreamMachine = setup({
  types: {
    context: {} as ChatStreamContext,
    events: {} as ChatStreamEvent,
    input: {} as ChatStreamInput
  },
  actions: {
    appendChunk: chatStreamEffects.appendChunk,
    clearBuffer: chatStreamEffects.clearBuffer,
    rememberCorrelation: assign(chatStreamAssigns.rememberCorrelation),
    rememberServerIds: assign(chatStreamAssigns.rememberServerIds),
    captureError: assign(chatStreamAssigns.captureError),
    captureMeta: assign(chatStreamAssigns.captureMeta),
    forgetCorrelation: assign(chatStreamAssigns.forgetCorrelation)
  }
}).createMachine({
  id: 'chatStream',
  initial: 'idle',
  context: ({ input }) => ({
    chatId: input.chatId,
    buffer: input.buffer
  }),
  on: {
    [CHAT_STREAM_EVENT.RESET]: {
      target: '.idle',
      actions: ['clearBuffer', 'forgetCorrelation']
    },
    [CHAT_STREAM_EVENT.START]: {
      target: '.connecting',
      actions: ['clearBuffer', 'rememberCorrelation']
    },
    [CHAT_STREAM_EVENT.TURN_PERSISTED]: {
      actions: 'rememberServerIds'
    }
  },
  states: {
    idle: {},

    connecting: {
      on: {
        [CHAT_STREAM_EVENT.CONNECTED]: 'streaming',
        [CHAT_STREAM_EVENT.STREAM_ERROR]: {
          target: 'error',
          actions: 'captureError'
        },
        [CHAT_STREAM_EVENT.ABORT]: 'cancelling'
      }
    },

    streaming: {
      on: {
        [CHAT_STREAM_EVENT.STREAM_CHUNK]: { actions: 'appendChunk' },
        [CHAT_STREAM_EVENT.STREAM_DONE]: {
          target: 'done',
          actions: 'captureMeta'
        },
        [CHAT_STREAM_EVENT.STREAM_ERROR]: {
          target: 'error',
          actions: 'captureError'
        },
        [CHAT_STREAM_EVENT.ABORT]: 'cancelling',
        [CHAT_STREAM_EVENT.INTERRUPT]: {}
      }
    },

    cancelling: {
      on: {
        [CHAT_STREAM_EVENT.STREAM_CHUNK]: { actions: 'appendChunk' },
        [CHAT_STREAM_EVENT.STREAM_CANCELLED]: 'aborted',
        [CHAT_STREAM_EVENT.STREAM_DONE]: {
          target: 'done',
          actions: 'captureMeta'
        },
        [CHAT_STREAM_EVENT.STREAM_ERROR]: {
          target: 'error',
          actions: 'captureError'
        }
      }
    },

    done: {},

    error: {
      on: {
        [CHAT_STREAM_EVENT.RETRY]: 'connecting'
      }
    },

    aborted: {}
  }
});
